import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * Checkbox Component (Preline UI)
 *
 * Checkbox input with optional label following Preline design patterns.
 * Supports indeterminate state for "select all" functionality.
 *
 * @param {Object} props
 * @param {string} [props.label] - Checkbox label text (optional)
 * @param {boolean} props.checked - Whether checkbox is checked
 * @param {Function} props.onChange - Callback when checked state changes (receives boolean)
 * @param {boolean} [props.disabled=false] - Whether checkbox is disabled
 * @param {boolean} [props.indeterminate=false] - Whether checkbox is in indeterminate state (shows dash)
 * @param {string} [props.name] - Checkbox name attribute
 * @param {string} [props.id] - Checkbox id attribute
 * @param {string} [props.className] - Additional classes for wrapper div
 *
 * @example
 * // Without label (for table cells)
 * <Checkbox checked={isSelected} onChange={(checked) => handleToggle(checked)} />
 *
 * @example
 * // With label
 * <Checkbox label="Accept terms" checked={accepted} onChange={setAccepted} />
 *
 * @example
 * // Indeterminate state (for select all with partial selection)
 * <Checkbox checked={false} indeterminate={true} onChange={handleSelectAll} />
 */
export function Checkbox({ label, checked, onChange, disabled = false, indeterminate = false, name, id, className = '' }) {
  const checkboxRef = useRef(null);
  const checkboxId = id || name || label?.toLowerCase().replace(/\s+/g, '-');

  // Set indeterminate property (cannot be set via HTML attribute)
  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <div className={`flex ${className}`}>
      <input
        ref={checkboxRef}
        type="checkbox"
        className="shrink-0 size-4 border border-brand/30 rounded text-brand bg-white focus:outline-none focus:ring-0 checked:bg-brand checked:border-transparent active:border-transparent indeterminate:bg-brand indeterminate:border-transparent disabled:opacity-50 disabled:pointer-events-none cursor-pointer"
        id={checkboxId}
        name={name}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      {label && (
        <label htmlFor={checkboxId} className="text-sm text-muted ms-3 cursor-pointer">
          {label}
        </label>
      )}
    </div>
  );
}

Checkbox.propTypes = {
  label: PropTypes.string,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  indeterminate: PropTypes.bool,
  name: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
};
